import { localDate } from '../../components/dateRange';
export { localDate, monthRange } from '../../components/dateRange';

export const formatDate = value => value ? new Date(String(value).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
export function daysUntil(value, today = localDate()) {
  return value ? Math.round((Date.parse(String(value).slice(0, 10)) - Date.parse(today)) / 86400000) : null;
}
export const relativeDue = days => days === null ? 'Not scheduled' : days < 0 ? `${Math.abs(days)} days overdue` : days === 0 ? 'Due today' : `In ${days} day${days === 1 ? '' : 's'}`;
export const retired = item => item.decommissioned || item.operationalStatus === 'Decommissioned';

export function patStatus(item, today) {
  if (!item.requiresPat) return 'Not Required';
  if (String(item.lastTestResult).toLowerCase() === 'fail') return 'Failed';
  const days = daysUntil(item.nextPatDue, today);
  if (days !== null && days < 0) return 'Overdue';
  if (days !== null && days <= 30) return 'Due Soon (≤ 30 days)';
  return String(item.lastTestResult).toLowerCase() === 'pass' && days !== null ? 'Pass' : 'Not Recorded';
}

// These are register checks based on stored operational/PAT/maintenance fields.
// They do not imply a statutory certificate or a recorded status history.
export function complianceStatus(item, today) {
  const pat = patStatus(item, today);
  const maintenance = item.requiresRoutineMaintenance ? daysUntil(item.nextMaintenanceDate, today) : null;
  if (retired(item)) return 'Attention';
  if (item.operationalStatus === 'Out of Service' || ['Failed', 'Overdue'].includes(pat) || (maintenance !== null && maintenance < 0)) return 'Non Compliant';
  if (item.operationalStatus !== 'In Service' || !['good', 'excellent'].includes(String(item.condition).toLowerCase()) ||
    ['Not Recorded', 'Due Soon (≤ 30 days)'].includes(pat) || (item.requiresRoutineMaintenance && (maintenance === null || maintenance <= 30))) return 'Attention';
  return 'Compliant';
}

export function summarizeEquipment(records, today) {
  const total = records.length;
  const count = predicate => records.filter(predicate).length;
  const percentage = value => total ? Math.round(value * 1000 / total) / 10 : 0;
  const compliant = count(item => complianceStatus(item, today) === 'Compliant');
  const attention = count(item => complianceStatus(item, today) === 'Attention');
  const nonCompliant = count(item => complianceStatus(item, today) === 'Non Compliant');
  return { total, compliant, attention, nonCompliant, percentage,
    operational: count(item => !retired(item) && item.operationalStatus === 'In Service'),
    locations: new Set(records.map(item => item.location).filter(Boolean)).size,
    overdue: count(item => !retired(item) && item.requiresRoutineMaintenance && daysUntil(item.nextMaintenanceDate, today) !== null && daysUntil(item.nextMaintenanceDate, today) < 0),
    dueSoon: count(item => !retired(item) && item.requiresRoutineMaintenance && daysUntil(item.nextMaintenanceDate, today) !== null && daysUntil(item.nextMaintenanceDate, today) >= 0 && daysUntil(item.nextMaintenanceDate, today) <= 7),
  };
}

export function documentUrl(value) {
  if (!value) return '';
  try {
    const base = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || window.location.origin;
    const url = new URL(value, base + '/');
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch { return ''; }
}

export function equipmentCsv(records) {
  const rows = [['Equipment', 'Asset ID', 'Category', 'Location', 'Status', 'Condition', 'PAT result', 'Next PAT due', 'Next maintenance', 'Registered'],
    ...records.map(item => [item.name, item.assetId, item.category, item.location, item.operationalStatus, item.condition, item.lastTestResult, item.nextPatDue, item.nextMaintenanceDate, item.createdAt])];
  return '\uFEFF' + rows.map(row => row.map(value => {
    let text = String(value ?? '');
    if (/^[=+@\-\t\r]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  }).join(',')).join('\r\n');
}

export function registrationSparkline(records, range, predicate) {
  const start = Date.parse(range.startDate), end = Date.parse(range.endDate);
  const totals = Array.from({ length: 8 }, (_, index) => records.filter(item => predicate(item) && Date.parse(item.createdAt.slice(0, 10)) <= start + (end - start) * index / 7).length);
  const peak = Math.max(1, ...totals);
  return totals.map((total, index) => `${Math.round(index * 100 / 7)},${35 - Math.round(total * 30 / peak)}`).join(' ');
}
